import { getActivePreviewCompany, PREVIEW_ACTOR_ID } from './flag';
import { previewData } from './data';
import { isoToday } from '../dates';

// Preview Mode emulation of the Management Notebook (Phase 8.10–8.12, D-221/D-222/D-223;
// migrations 0146/0147). The goals and marks live in the preview dataset's `notebook_goals` /
// `notebook_entries` tables, so the page's direct RLS-style `.from(...)` reads see them exactly as
// they would see the real tables. The call list and calibration are a DETERMINISTIC APPROXIMATION
// — each preview customer gets a fixed open amount and due date; "actual" is a fixed share of
// "expected" — for UI verification only. The real rules live in the database.

type Row = Record<string, unknown>;
type Result<T> = { data: T; error: null | { message: string } };

const FNS = new Set([
  'fn_notebook_goal_create', 'fn_notebook_goal_close', 'fn_notebook_entry_create',
  'fn_notebook_call_list', 'fn_notebook_goal_calibration',
]);
const replays = new Map<string, { hash: string; result: Row }>();

const text = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
const fail = (message: string): Result<null> => ({ data: null, error: { message } });

function shift(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
function toUnits(v: string): bigint {
  const [whole = '0', frac = ''] = v.split('.');
  return BigInt(whole) * 10_000n + BigInt(frac.padEnd(4, '0').slice(0, 4));
}
function fromUnits(u: bigint): string {
  const neg = u < 0n;
  const a = neg ? -u : u;
  return `${neg ? '-' : ''}${a / 10_000n}.${String(a % 10_000n).padStart(4, '0')}`;
}

function table(companyId: string, name: 'notebook_goals' | 'notebook_entries'): Row[] {
  const tables = previewData(companyId).tables;
  return [...(tables[name] ?? [])];
}
function save(companyId: string, name: 'notebook_goals' | 'notebook_entries', rows: Row[]): void {
  previewData(companyId).tables[name] = rows;
}

function once(companyId: string, key: unknown, hash: string, act: () => Result<Row | null>): Result<Row | null> {
  const k = text(key).trim();
  if (k === '') return fail('NOTEBOOK_INVALID_PAYLOAD');
  const prior = replays.get(`${companyId}:${k}`);
  if (prior !== undefined) return prior.hash === hash ? { data: prior.result, error: null } : fail('IDEMPOTENCY_CONFLICT');
  const r = act();
  if (r.error === null && r.data !== null) replays.set(`${companyId}:${k}`, { hash, result: r.data });
  return r;
}

/** Each preview customer's fixed open amount and due date (some overdue, some far out). */
function openDebts(companyId: string): { customerId: string; name: string | null; due: string; amount: string }[] {
  const customers = (previewData(companyId).tables['customers'] ?? []) as Row[];
  const today = isoToday();
  return customers.map((c, i) => ({
    customerId: text(c['id']),
    name: (c['name'] as string | null | undefined) ?? null,
    due: shift(today, -24 + i * 6),
    amount: `${1500 + ((i * 3701) % 9000)}.00`,
  }));
}

function findGoal(companyId: string, goalId: unknown): Row | undefined {
  return table(companyId, 'notebook_goals').find((g) => g['id'] === goalId);
}

function listFor(companyId: string, goal: Row): { start: string; end: string; items: Row[] } {
  const start = text(goal['created_at']).slice(0, 10);
  const end = shift(text(goal['target_date']), Number(goal['window_days']));
  const items = openDebts(companyId)
    .filter((d) => d.due <= end)
    .sort((a, b) => (toUnits(b.amount) > toUnits(a.amount) ? 1 : toUnits(b.amount) < toUnits(a.amount) ? -1
      : (a.name ?? '').localeCompare(b.name ?? '') || a.customerId.localeCompare(b.customerId)))
    .map((d) => ({ customerId: d.customerId, customerName: d.name, effectiveDueDate: d.due, openAmount: fromUnits(toUnits(d.amount)) }));
  return { start, end, items };
}

/** The 0146/0147 Notebook RPCs; `undefined` for any other function. */
export function previewNotebookRpc(fn: string, args: Row): Result<unknown> | undefined {
  if (!FNS.has(fn)) return undefined;
  const companyId = text(args['p_company']) || getActivePreviewCompany();
  const now = new Date().toISOString();

  switch (fn) {
    case 'fn_notebook_goal_create': {
      const amount = text(args['p_target_amount']).trim();
      const date = text(args['p_target_date']);
      const windowDays = Number(args['p_window_days']);
      if (!/^\d{1,16}(\.\d{1,4})?$/.test(amount) || !/[1-9]/.test(amount) || date < isoToday()
          || !Number.isInteger(windowDays) || windowDays < 0) {
        return fail('NOTEBOOK_INVALID_PAYLOAD');
      }
      return once(companyId, args['p_idempotency_key'], `create:${amount}:${date}:${windowDays}`, () => {
        const id = `nb-goal-${table(companyId, 'notebook_goals').length + 1}-${Date.now()}`;
        save(companyId, 'notebook_goals', [...table(companyId, 'notebook_goals'), {
          id, company_id: companyId, target_amount: fromUnits(toUnits(amount)), target_date: date, window_days: windowDays,
          note: null, status: 'OPEN', created_by: PREVIEW_ACTOR_ID, created_at: now, closed_by: null, closed_at: null,
        }]);
        return { data: { ok: true, goalId: id, status: 'OPEN' }, error: null };
      });
    }
    case 'fn_notebook_goal_close': {
      const goal = findGoal(companyId, args['p_goal_id']);
      if (goal === undefined) return fail('NOTEBOOK_GOAL_NOT_FOUND');
      return once(companyId, args['p_idempotency_key'], `close:${text(goal['id'])}`, () => {
        if (goal['status'] === 'CLOSED') return fail('NOTEBOOK_GOAL_ALREADY_CLOSED');
        save(companyId, 'notebook_goals', table(companyId, 'notebook_goals').map((g) =>
          g['id'] === goal['id'] ? { ...g, status: 'CLOSED', closed_by: PREVIEW_ACTOR_ID, closed_at: now } : g));
        return { data: { ok: true, goalId: goal['id'], status: 'CLOSED' }, error: null };
      });
    }
    case 'fn_notebook_entry_create': {
      const type = text(args['p_entry_type']);
      const raw = args['p_marked_amount'];
      const amount = raw === null || raw === undefined ? null : text(raw).trim();
      const shapeOk = (type === 'PAID_FULL' && amount === null)
        || (type === 'PAID_PARTIAL' && amount !== null && /^\d{1,16}(\.\d{1,4})?$/.test(amount) && /[1-9]/.test(amount));
      if (!shapeOk) return fail('NOTEBOOK_ENTRY_INVALID_TYPE');
      const goal = findGoal(companyId, args['p_goal_id']);
      if (goal === undefined) return fail('NOTEBOOK_GOAL_NOT_FOUND');
      return once(companyId, args['p_idempotency_key'], `mark:${text(goal['id'])}:${text(args['p_customer_id'])}:${type}:${amount ?? ''}`, () => {
        if (goal['status'] !== 'OPEN') return fail('NOTEBOOK_GOAL_CLOSED');
        const id = `nb-entry-${table(companyId, 'notebook_entries').length + 1}-${Date.now()}`;
        save(companyId, 'notebook_entries', [...table(companyId, 'notebook_entries'), {
          id, company_id: companyId, goal_id: goal['id'], customer_id: args['p_customer_id'], entry_type: type,
          marked_amount: amount === null ? null : fromUnits(toUnits(amount)), created_by: PREVIEW_ACTOR_ID, created_at: now,
        }]);
        return { data: { ok: true, entryId: id, goalId: goal['id'], customerId: args['p_customer_id'], entryType: type }, error: null };
      });
    }
    case 'fn_notebook_call_list': {
      const goal = findGoal(companyId, args['p_goal_id']);
      if (goal === undefined) return fail('NOTEBOOK_GOAL_NOT_FOUND');
      const { start, end, items } = listFor(companyId, goal);
      return { data: { ok: true, goalId: goal['id'], goalStart: start, windowEnd: end, count: items.length, items }, error: null };
    }
    case 'fn_notebook_goal_calibration': {
      const goal = findGoal(companyId, args['p_goal_id']);
      if (goal === undefined) return fail('NOTEBOOK_GOAL_NOT_FOUND');
      const { start, end, items } = listFor(companyId, goal);
      const expected = items.reduce((s, r) => s + toUnits(text(r['openAmount'])), 0n);
      const actual = (expected * 35n) / 100n; // a fixed demo share, not a rule
      const target = toUnits(text(goal['target_amount']));
      return {
        data: {
          ok: true, goalId: goal['id'], status: goal['status'], goalStart: start, today: isoToday(), windowEnd: end,
          targetAmount: fromUnits(target), expected: fromUnits(expected), actual: fromUnits(actual),
          collectionGap: fromUnits(target - actual),
        },
        error: null,
      };
    }
  }
  return undefined;
}
